'use client';

import { c as _c } from "react/compiler-runtime";
import { useLocale } from 'next-intl';
import { useMemo } from 'react';
import useSWRInfinite from 'swr/infinite';
import { SWIMLANE_PAGE_LIMIT } from '@/app/[locale]/(worlds)/components/useExploreAllSectionsData';
import { useUAContext } from '@/context/UAContext';
import { useSavedPages, useScrollRestorationSWR } from '@/hooks/useScrollRestoration';
import { getNewPageGameScore } from '@/lib/configs';
import { isGamesOnlyContentTypes } from '@/lib/search';
import type { Locale } from '@/server/constants/locales';
import type { ContentTypeCode, PaginatedResponse, RoomDetail } from '@/services/cms';
import { getPublicRoomsV3 } from '@/services/cms';
const GAMES_PAGE_LIMIT = 8;
const SECTION_SORT = 'last_updated_at';
type SectionPage = PaginatedResponse<RoomDetail>;
export type NewReleasesAllSectionsPage = {
  games: RoomDetail[];
  videos: RoomDetail[];
  experiences: RoomDetail[];
};
function makeGetKey(contentTypes: ContentTypeCode[], limit: number) {
  return (pageIndex: number, previousPageData: SectionPage | null) => {
    const params = new URLSearchParams({
      start: '0',
      limit: limit.toString(),
      sort: SECTION_SORT,
      content_types: contentTypes.join(',')
    });
    if (pageIndex === 0) return `newReleasesSections?${params.toString()}`;
    if (!previousPageData?.results) return null;
    const next = previousPageData.pages?.next;
    if (!next) return null;
    const start = new URLSearchParams(next.split('?')[1]).get('start');
    params.set('start', start || '0');
    return `newReleasesSections?${params.toString()}`;
  };
}
function makeFetcher(htcToken: string | undefined, locale: Locale, totalVramKb: number | undefined, contentTypes: ContentTypeCode[], limit: number) {
  return (key: string): Promise<SectionPage | undefined> => {
    const query = key.split('?')[1];
    const params = new URLSearchParams(query);
    const start = Number(params.get('start'));
    return getPublicRoomsV3({
      sort: SECTION_SORT,
      limit,
      start,
      locale,
      content_types: contentTypes,
      exclude_persona: true,
      ...(isGamesOnlyContentTypes(contentTypes) && {
        game_score: getNewPageGameScore()
      }),
      ...(contentTypes.includes('experiences') && {
        voob: true
      }),
      ...(totalVramKb && {
        total_vram_kb: totalVramKb
      })
    }, htcToken, false).then(res => res?.results);
  };
}
type UseNewReleasesAllSectionsDataParams = {
  htcToken?: string;
};
export function useNewReleasesAllSectionsData(t0) {
  const $ = _c(52);
  const {
    htcToken
  } = t0;
  const locale = useLocale() as Locale;
  const {
    totalVramKb
  } = useUAContext();
  const saved = useSavedPages();
  let t1;
  if ($[0] !== saved.savedPages) {
    t1 = {
      revalidateFirstPage: false,
      initialSize: saved.savedPages
    };
    $[0] = saved.savedPages;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const swrOptions = t1;
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = makeGetKey(["games"], GAMES_PAGE_LIMIT);
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== htcToken || $[4] !== locale || $[5] !== totalVramKb) {
    t3 = makeFetcher(htcToken, locale, totalVramKb, ["games"], GAMES_PAGE_LIMIT);
    $[3] = htcToken;
    $[4] = locale;
    $[5] = totalVramKb;
    $[6] = t3;
  } else {
    t3 = $[6];
  }
  const {
    data: gamesData,
    isLoading: gamesLoading,
    isValidating: gamesValidating,
    size: gamesSize,
    setSize: setGamesSize
  } = useSWRInfinite(t2, t3, swrOptions);
  let t4;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = makeGetKey(["videos"], SWIMLANE_PAGE_LIMIT);
    $[7] = t4;
  } else {
    t4 = $[7];
  }
  let t5;
  if ($[8] !== htcToken || $[9] !== locale || $[10] !== totalVramKb) {
    t5 = makeFetcher(htcToken, locale, totalVramKb, ["videos"], SWIMLANE_PAGE_LIMIT);
    $[8] = htcToken;
    $[9] = locale;
    $[10] = totalVramKb;
    $[11] = t5;
  } else {
    t5 = $[11];
  }
  const {
    data: videosData,
    isLoading: videosLoading,
    isValidating: videosValidating,
    size: videosSize,
    setSize: setVideosSize
  } = useSWRInfinite(t4, t5, swrOptions);
  let t6;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = makeGetKey(["experiences"], SWIMLANE_PAGE_LIMIT);
    $[12] = t6;
  } else {
    t6 = $[12];
  }
  let t7;
  if ($[13] !== htcToken || $[14] !== locale || $[15] !== totalVramKb) {
    t7 = makeFetcher(htcToken, locale, totalVramKb, ["experiences"], SWIMLANE_PAGE_LIMIT);
    $[13] = htcToken;
    $[14] = locale;
    $[15] = totalVramKb;
    $[16] = t7;
  } else {
    t7 = $[16];
  }
  const {
    data: experiencesData,
    isLoading: experiencesLoading,
    isValidating: experiencesValidating,
    size: experiencesSize,
    setSize: setExperiencesSize
  } = useSWRInfinite(t6, t7, swrOptions);
  const size = Math.max(gamesData?.length ?? 0, videosData?.length ?? 0, experiencesData?.length ?? 0);
  let t8;
  if ($[17] !== experiencesData || $[18] !== gamesData || $[19] !== size || $[20] !== videosData) {
    let t9;
    if ($[22] !== experiencesData || $[23] !== gamesData || $[24] !== videosData) {
      t9 = (_, index) => ({
        games: gamesData?.[index]?.results ?? [],
        videos: videosData?.[index]?.results ?? [],
        experiences: experiencesData?.[index]?.results ?? []
      });
      $[22] = experiencesData;
      $[23] = gamesData;
      $[24] = videosData;
      $[25] = t9;
    } else {
      t9 = $[25];
    }
    t8 = Array.from({
      length: size
    }, t9);
    $[17] = experiencesData;
    $[18] = gamesData;
    $[19] = size;
    $[20] = videosData;
    $[21] = t8;
  } else {
    t8 = $[21];
  }
  const pages = t8;
  const gamesLoadedTotal = gamesData?.[0]?.pages?.total ?? 0;
  const videosLoadedTotal = videosData?.[0]?.pages?.total ?? 0;
  const experiencesLoadedTotal = experiencesData?.[0]?.pages?.total ?? 0;
  let t9;
  if ($[26] !== gamesData) {
    t9 = gamesData?.flatMap(_temp).length ?? 0;
    $[26] = gamesData;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  const loadedGames = t9;
  let t10;
  if ($[28] !== videosData) {
    t10 = videosData?.flatMap(_temp2).length ?? 0;
    $[28] = videosData;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  const loadedVideos = t10;
  let t11;
  if ($[30] !== experiencesData) {
    t11 = experiencesData?.flatMap(_temp3).length ?? 0;
    $[30] = experiencesData;
    $[31] = t11;
  } else {
    t11 = $[31];
  }
  const loadedExperiences = t11;
  const enableLoadMore = loadedGames < gamesLoadedTotal || loadedVideos < videosLoadedTotal || loadedExperiences < experiencesLoadedTotal;
  const isLoading = gamesLoading || videosLoading || experiencesLoading;
  const isValidating = gamesValidating || videosValidating || experiencesValidating;
  const size_0 = Math.min(gamesSize, videosSize, experiencesSize);
  let t12;
  if ($[32] !== setExperiencesSize || $[33] !== setGamesSize || $[34] !== setVideosSize) {
    t12 = nextSize => {
      setGamesSize(nextSize);
      setVideosSize(nextSize);
      setExperiencesSize(nextSize);
    };
    $[32] = setExperiencesSize;
    $[33] = setGamesSize;
    $[34] = setVideosSize;
    $[35] = t12;
  } else {
    t12 = $[35];
  }
  const setSize = t12;
  let t13;
  if ($[36] !== isLoading || $[37] !== isValidating || $[38] !== setSize || $[39] !== size_0) {
    t13 = {
      size: size_0,
      setSize,
      isLoading,
      isValidating
    };
    $[36] = isLoading;
    $[37] = isValidating;
    $[38] = setSize;
    $[39] = size_0;
    $[40] = t13;
  } else {
    t13 = $[40];
  }
  const trackedSetSize = useScrollRestorationSWR(saved, t13);
  let t14;
  if ($[41] !== isLoading || $[42] !== isValidating || $[43] !== size_0 || $[44] !== trackedSetSize) {
    t14 = {
      isLoading,
      isValidating,
      setSize: trackedSetSize,
      size: size_0
    };
    $[41] = isLoading;
    $[42] = isValidating;
    $[43] = size_0;
    $[44] = trackedSetSize;
    $[45] = t14;
  } else {
    t14 = $[45];
  }
  let t15;
  if ($[46] !== enableLoadMore || $[47] !== isLoading || $[48] !== isValidating || $[49] !== pages || $[50] !== t14) {
    t15 = {
      pages,
      gamesPageLimit: GAMES_PAGE_LIMIT,
      enableLoadMore,
      isLoading,
      isValidating,
      pagination: t14
    };
    $[46] = enableLoadMore;
    $[47] = isLoading;
    $[48] = isValidating;
    $[49] = pages;
    $[50] = t14;
    $[51] = t15;
  } else {
    t15 = $[51];
  }
  return t15;
}
function _temp3(page_1) {
  return page_1?.results ?? [];
}
function _temp2(page_0) {
  return page_0?.results ?? [];
}
function _temp(page) {
  return page?.results ?? [];
}